import type { VFrameBase } from '@festina/life-general-types/life'
import type { AllKeys, Except, Merge, Optional } from '@festina-life/core'
import {
  FormikForm,
  type FormikFormProps,
  isYupObjectSchema,
} from '@festina-life/ui/formikForms'
import { ExistingValuesProvider, useDataContext } from '@festina-life/ui/theme'
import { useSuspenseQuery } from '@tanstack/react-query'
import isEqual from 'lodash-es/isEqual'
import { useCallback, useState } from 'react'
import type * as Yup from 'yup'

import { useScenarioContext } from '../../context/scenarioContext'
import { queryKeyForFetchFrame } from './formUtility'
import { FrameProvider } from './FrameProvider'
import { useCreateYupSchema } from './yup/useCreateYupSchema'

type LifeFormBaseProps<
  T extends { _type: string },
  F extends VFrameBase,
> = Merge<
  Except<FormikFormProps<T>, 'initialValues' | 'validationSchema'>,
  {
    onSubmit?: (values: T) => void
    // Unfortunately this must be optional until the CommandModalButton can pass it
    fetchFrame?: (values: Partial<T> & { _type: string }) => Promise<F | null>
    mapInitialValues: (frame: Optional<F>) => T
    onFrameIsLoadingChanged: (isLoading: boolean) => void
    relevantFrameFields?: Extract<AllKeys<T>, string>[]
    validationSchema?:
      | NonNullable<FormikFormProps<T>['validationSchema']>
      | ((
          frame: Optional<F>,
        ) => NonNullable<FormikFormProps<T>['validationSchema']>)
    existingValues?: T
  }
>

export type LifeFormProps<
  T extends { _type: string },
  F extends VFrameBase,
> = LifeFormBaseProps<T, F> &
  (
    | {
        customVFrameGetter: ({
          name,
          frame,
          values,
        }: {
          name: string
          frame: Optional<F>
          values: T
        }) => Optional<VFrameBase>
      }
    | {
        customVFrameGetter?: never
      }
  )

/**
 *
 * @beta we have some un-resolved issues/questions regarding cleanup of values APG2223-2417
 */
export const LifeLazyForm = <
  T extends { _type: string },
  F extends VFrameBase,
>({
  // If fetchFrame ended up not being passed on accident, treat it as if there is no frame.
  fetchFrame = () => Promise.resolve(null),
  mapInitialValues,
  children,
  validationSchema,
  existingValues,
  onFrameIsLoadingChanged,
  onSubmit,
  onInputChange,
  shouldFieldsBeDisabled = false,
  customVFrameGetter,
  relevantFrameFields,
  // Spread is used to not forget to pass down props.
  ...other
}: LifeFormProps<T, F>) => {
  const createYupSchema = useCreateYupSchema()

  const { shouldSkipFrame, festinaComponent } = useDataContext()
  const { scenarioIdForRequestHeader } = useScenarioContext()

  const emptyValues = mapInitialValues(undefined)
  const { data: initialFrame } = useSuspenseQuery({
    // Should be in-sync with the FrameProvider fetch
    queryKey: shouldSkipFrame
      ? []
      : queryKeyForFetchFrame({
          festinaComponent,
          fetchFrameArg: emptyValues,
          scenarioId: scenarioIdForRequestHeader,
          frameQueryKeySuffix: other.formId,
        }),
    queryFn: async ({ queryKey }) => {
      const fetchArg = queryKey[2]
      if (!fetchArg) {
        return null
      }
      try {
        return await fetchFrame(fetchArg)
      } catch {
        return null
      }
    },
    staleTime: 0,
  })
  const initialValues = mapInitialValues(initialFrame)
  const [frame, setFrame] = useState(initialFrame)

  const yupSchemaFromFrame = useCallback(
    (frame: Optional<F>) => {
      const frameValidation =
        frame && frame._type.endsWith('VFrame')
          ? (createYupSchema(frame) as Yup.ObjectSchema<Yup.AnyObject>)
          : null

      const otherSchema =
        typeof validationSchema === 'function'
          ? validationSchema(frame)
          : validationSchema

      return isYupObjectSchema(otherSchema) &&
        isYupObjectSchema(frameValidation)
        ? otherSchema.concat(frameValidation)
        : (frameValidation ?? otherSchema)
    },
    [validationSchema, createYupSchema],
  )

  const [lifeFormValidationSchema, setLifeFormValidationSchema] = useState(
    yupSchemaFromFrame(frame),
  )

  const handleFrameChanged = useCallback(
    (frame: Optional<F>) => {
      setFrame((oldFrame) =>
        // Avoid re-renders if the fetched frame is exactly the same.
        isEqual(frame, oldFrame) ? oldFrame : (frame ?? null),
      )
      setLifeFormValidationSchema(yupSchemaFromFrame(frame))
    },
    [yupSchemaFromFrame],
  )

  return (
    <FormikForm<T>
      validationSchema={
        // shouldFieldsBeDisabled is only provided if we are reading in a state that should not
        // have any validation. Because else we might suddenly be hit by some new validation on an old command or frames change
        shouldFieldsBeDisabled ? undefined : lifeFormValidationSchema
      }
      initialStatus={{ hasFormValidated: false }}
      initialValues={initialValues}
      onSubmit={onSubmit}
      onInputChange={onInputChange}
      shouldFieldsBeDisabled={shouldFieldsBeDisabled}
      {...other}
    >
      {({ resetForm, ...formikProps }) => (
        <ExistingValuesProvider<T> existingValues={existingValues}>
          <FrameProvider
            fetchFrame={fetchFrame}
            customVFrameGetter={customVFrameGetter}
            relevantFrameFields={relevantFrameFields}
            frame={frame}
            onFrameChanged={handleFrameChanged}
            onFrameIsLoadingChanged={onFrameIsLoadingChanged}
          >
            {typeof children === 'function'
              ? children({
                  ...formikProps,
                  resetForm: (formikState) =>
                    resetForm({ ...formikState, status: formikProps.status }), // We don't want to reset the status to the initial one, but keep the current one.
                })
              : children}
          </FrameProvider>
        </ExistingValuesProvider>
      )}
    </FormikForm>
  )
}
